<template>
  <div class="bbm-events-page">

    <!-- Hero -->
    <section class="bbm-events-hero">
      <div class="bbm-events-hero__content">
        <h1 class="bbm-events-hero__title">Events in Brussels</h1>
        <p class="bbm-events-hero__sub">Stay up to date with the latest events, meetups and conferences in the Brussels innovation ecosystem.</p>
      </div>
    </section>

    <main class="bbm-events-main">

      <!-- Search + count -->
      <div class="tw-flex justify-between items-center" style="margin-bottom: 20px;">
        <p v-if="!loading" class="bbm-events-count">
          {{ filteredEvents.length }} {{ filteredEvents.length === 1 ? 'event' : 'events' }}
        </p>
        <div class="bbm-search">
          <span class="material-symbols-outlined bbm-search__icon">search</span>
          <input v-model="searchQuery" class="bbm-search__input" placeholder="Search events..." type="text"/>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="bbm-filters" style="margin-bottom: 32px;">

        <!-- Period -->
        <div class="bbm-filter-group">
          <button
            v-for="p in periods"
            :key="p.value"
            class="bbm-filter-chip"
            :class="{ 'bbm-filter-chip--active': filterPeriod === p.value }"
            @click="filterPeriod = p.value"
          >
            {{ p.label }}
          </button>
        </div>

        <!-- Language (only shown when >1 language in the data) -->
        <template v-if="availableLanguages.length > 1">
          <div class="bbm-filter-sep"/>
          <div class="bbm-filter-group">
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              class="bbm-filter-chip"
              :class="{ 'bbm-filter-chip--active': filterLanguages.includes(lang.code) }"
              @click="toggleLanguage(lang.code)"
            >
              {{ lang.label }}
            </button>
          </div>
        </template>

        <!-- Clear -->
        <button v-if="hasActiveFilters" class="bbm-filter-clear" @click="clearFilters">
          <span class="material-symbols-outlined" style="font-size:14px; line-height:1;">close</span>
          Clear
        </button>

      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="n in 6" :key="n" class="bbm-skeleton-card">
          <div class="bbm-skeleton bbm-skeleton--image"></div>
          <div style="padding: 20px;">
            <div class="bbm-skeleton bbm-skeleton--organiser"></div>
            <div class="bbm-skeleton bbm-skeleton--title"></div>
            <div class="bbm-skeleton bbm-skeleton--text"></div>
            <div class="bbm-skeleton bbm-skeleton--text" style="width: 60%"></div>
          </div>
        </div>
      </div>

      <!-- Events grid -->
      <div v-else-if="filteredEvents.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="event in filteredEvents"
          :key="event.id"
          class="bbm-event-card"
          @click="openEvent(event)"
        >
          <!-- Media -->
          <div class="bbm-event-card__media" :class="{ 'bbm-event-card__media--placeholder': !event.image }">
            <img v-if="event.image" :src="getImageForAnnouncement(event)" :alt="event.title" class="bbm-event-card__img"/>
            <div v-else class="bbm-event-card__placeholder">
              <span class="material-symbols-outlined bbm-event-card__placeholder-icon">event</span>
            </div>

            <!-- Date badge -->
            <div class="bbm-event-card__date-badge">
              <span class="bbm-event-card__date-day">{{ formatDay(event.start_date) }}</span>
              <span class="bbm-event-card__date-month">{{ formatMonth(event.start_date) }}</span>
            </div>

            <!-- Language badge -->
            <span v-if="event.locale && event.locale !== 'en'" class="bbm-event-card__lang-badge">
              {{ event.locale.toUpperCase() }}
            </span>

          </div>

          <!-- Body -->
          <div class="bbm-event-card__body">
            <h3 class="bbm-event-card__title">{{ event.title }}</h3>

            <p v-if="event.shortDescription" class="bbm-event-card__desc">{{ event.shortDescription }}</p>

            <div class="bbm-event-card__metas">
              <div class="bbm-event-card__meta">
                <span class="material-symbols-outlined">schedule</span>
                {{ formatDateTime(event.start_date) }}
                <template v-if="event.end_date && event.end_date !== event.start_date">
                  &ndash; {{ formatDateTime(event.end_date) }}
                </template>
              </div>
              <div v-if="eventLocation(event)" class="bbm-event-card__meta">
                <span class="material-symbols-outlined">location_on</span>
                {{ eventLocation(event) }}
              </div>
            </div>

            <div v-if="event.tags && event.tags.length" class="bbm-event-card__tags">
              <span v-for="tag in event.tags.slice(0, 3)" :key="tag.value" class="bbm-event-card__tag">
                {{ tag.label }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="bbm-empty">
        <span class="material-symbols-outlined bbm-empty__icon">{{ hasActiveFilters || searchQuery ? 'search_off' : 'event' }}</span>
        <p>{{ hasActiveFilters || searchQuery ? 'No events match your filters.' : 'No upcoming events.' }}</p>
        <button v-if="hasActiveFilters" class="bbm-empty-clear" @click="clearFilters">Clear filters</button>
      </div>

    </main>

    <SimplifiedFooterBrussels/>

    <!-- Event detail modal -->
    <BrusselsEventModal
      v-if="activeEvent"
      v-model="eventModalOpen"
      :event="activeEvent"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { ACTION_TYPES as NOTIFICATION_ACTION_TYPES } from '@/store/modules/notifications.js'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'
  import BrusselsEventModal from '@/components/Dashboard/BrusselsEventModal.vue'
  import { getImageForAnnouncement } from '@/util/helpers'

  const LANG_LABELS: Record<string, string> = {
    fr: 'FR',
    nl: 'NL',
    en: 'EN',
    de: 'DE',
  }

  export default defineComponent({
    name: 'StaticHomeBrusselsEvents',
    components: { SimplifiedFooterBrussels, BrusselsEventModal },
    data() {
      return {
        searchQuery: '' as string,
        activeEvent: null as any,
        eventModalOpen: false,
        filterPeriod: 'upcoming' as string,
        filterLanguages: [] as string[],
        periods: [
          { value: 'upcoming', label: 'Upcoming' },
          { value: 'week', label: 'This week' },
          { value: 'month', label: 'This month' },
          { value: 'all', label: 'All' },
        ],
      }
    },
    computed: {
      events(): any[] {
        return this.$store.state.notifications.listData.data || []
      },
      loading(): boolean {
        return this.$store.state.notifications.listData.loading
      },
      availableLanguages(): { code: string; label: string }[] {
        const codes = new Set<string>()
        this.events.forEach((e: any) => {
          if (e.locale && LANG_LABELS[e.locale]) codes.add(e.locale)
        })
        return [...codes]
          .sort()
          .map(code => ({ code, label: LANG_LABELS[code] }))
      },
      hasActiveFilters(): boolean {
        return this.filterPeriod !== 'upcoming' || this.filterLanguages.length > 0
      },
      filteredEvents(): any[] {
        let result = this.events

        // Search
        if (this.searchQuery.trim()) {
          const q = this.searchQuery.toLowerCase()
          result = result.filter((e: any) =>
            (e.title || '').toLowerCase().includes(q) ||
            (e.location || '').toLowerCase().includes(q)
          )
        }

        // Normalize date string: "2026-05-18 14:00:00" → "2026-05-18T14:00:00" (Safari fix)
        const parseDate = (s: string) => new Date(s.includes('T') ? s : s.replace(' ', 'T'))

        const now = new Date()
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

        // Strip past events unless "All" is selected
        if (this.filterPeriod !== 'all') {
          result = result.filter((e: any) => {
            if (!e.start_date) return false
            const ts = parseDate(e.start_date).getTime()
            return !isNaN(ts) && ts >= startOfToday
          })
        }

        // Period upper bound
        if (this.filterPeriod === 'week' || this.filterPeriod === 'month') {
          const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (7 - now.getDay()), 23, 59, 59, 999)
          const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)

          result = result.filter((e: any) => {
            const ts = parseDate(e.start_date).getTime()
            if (this.filterPeriod === 'week') return ts <= endOfWeek.getTime()
            if (this.filterPeriod === 'month') return ts <= endOfMonth.getTime()
            return true
          })
        }

        // Language
        if (this.filterLanguages.length > 0) {
          result = result.filter((e: any) =>
            this.filterLanguages.includes(e.locale)
          )
        }

        return result
      },
    },
    mounted() {
      this.$store.dispatch(NOTIFICATION_ACTION_TYPES.FETCH_NOTIFICATIONS_LIST, {
        limit: this.$store.state.notifications.listData.fetchLimit || 60,
        offset: null,
        filters: {
          is_event: true,
          by_start_date: true,
        },
      })
    },
    methods: {
      getImageForAnnouncement,
      toggleLanguage(code: string) {
        const idx = this.filterLanguages.indexOf(code)
        if (idx === -1) this.filterLanguages.push(code)
        else this.filterLanguages.splice(idx, 1)
      },
      clearFilters() {
        this.filterPeriod = 'upcoming'
        this.filterLanguages = []
        this.filterRegistration = false
      },
      formatDay(dateStr: string): string {
        if (!dateStr) return ''
        return new Date(dateStr).toLocaleDateString('en-BE', { day: '2-digit' })
      },
      formatMonth(dateStr: string): string {
        if (!dateStr) return ''
        return new Date(dateStr).toLocaleDateString('en-BE', { month: 'short' }).toUpperCase()
      },
      formatDateTime(dateStr: string): string {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        const date = d.toLocaleDateString('en-BE', { day: 'numeric', month: 'short' })
        const time = d.toLocaleTimeString('en-BE', { hour: '2-digit', minute: '2-digit' })
        return `${date} · ${time}`
      },
      eventLocation(event: any): string {
        if (event.address_line) return event.address_line
        const addr = event.context?.address
        if (!addr) return ''
        return [addr.city, addr.country].filter(Boolean).join(', ')
      },
      openEvent(event: any) {
        this.activeEvent = event
        this.eventModalOpen = true
      },
    },
  })
</script>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

  $bbm-primary: var(--primary-community);
  $bbm-surface: #f7f9fb;

  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  .bbm-events-page {
    font-family: 'Inter', sans-serif;
    background: $bbm-surface;
    color: #191c1e;
    min-height: 100vh;
  }

  /* ── Hero ── */
  .bbm-events-hero {
    background: $bbm-primary;
    padding: 64px 32px;
  }

  .bbm-events-hero__content {
    max-width: 1280px;
    margin: 0 auto;
  }

  .bbm-events-hero__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.75rem, 3vw, 3rem);
    font-weight: 700;
    color: white;
    margin: 0 0 12px;
    letter-spacing: -0.02em;
  }

  .bbm-events-hero__sub {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.75);
    margin: 0;
    max-width: 560px;
    line-height: 1.6;
  }

  /* ── Main layout ── */
  .bbm-events-main {
    max-width: 1280px;
    margin: 0 auto;
    padding: 48px 32px 80px;
  }

  .bbm-events-count {
    font-size: 0.9rem;
    color: #64748b;
    margin: 0;
  }

  /* ── Search bar ── */
  .bbm-search {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 12px;
    background: white;
  }

  .bbm-search__icon {
    color: #94a3b8;
    font-size: 1rem;
  }

  .bbm-search__input {
    border: none;
    outline: none;
    font-size: 0.875rem;
    width: 220px;
    background: transparent;
    color: #191c1e;

    &::placeholder {
      color: #94a3b8;
    }
  }

  /* ── Filter bar ── */
  .bbm-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    background: white;
    border: 1px solid #e8edf2;
    border-radius: 12px;
  }

  .bbm-filter-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .bbm-filter-sep {
    width: 1px;
    height: 20px;
    background: #e2e8f0;
    margin: 0 4px;
    flex-shrink: 0;
  }

  .bbm-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #64748b;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 9999px;
    padding: 4px 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
      border-color: $bbm-primary;
      color: $bbm-primary;
    }

    &--active {
      background: $bbm-primary;
      border-color: $bbm-primary;
      color: white;

      &:hover {
        opacity: 0.9;
        color: white;
      }
    }

  }

  .bbm-filter-clear {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;
    transition: color 0.15s;
    margin-left: 4px;

    &:hover {
      color: #475569;
    }
  }

  /* ── Event card ── */
  .bbm-event-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.3s ease, transform 0.25s ease;

    &:hover {
      box-shadow: 0 20px 48px rgba(0, 0, 0, 0.11);
      transform: translateY(-4px);

      .bbm-event-card__img {
        transform: scale(1.05);
      }
    }
  }

  /* ── Media area ── */
  .bbm-event-card__media {
    height: 196px;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    background: #e8edf2;

    &--placeholder {
      background: linear-gradient(135deg, $bbm-primary 0%, #003a4f 100%);
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.38) 100%);
      pointer-events: none;
    }
  }

  .bbm-event-card__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }

  .bbm-event-card__placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bbm-event-card__placeholder-icon {
    font-size: 3rem;
    color: rgba(255, 255, 255, 0.2);
  }

  /* ── Date badge ── */
  .bbm-event-card__date-badge {
    position: absolute;
    top: 14px;
    left: 14px;
    z-index: 2;
    background: white;
    border-radius: 10px;
    padding: 8px 12px;
    min-width: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  }

  .bbm-event-card__date-day {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
    color: $bbm-primary;
    line-height: 1;
  }

  .bbm-event-card__date-month {
    font-size: 0.6rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 2px;
  }

  /* ── Language badge ── */
  .bbm-event-card__lang-badge {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 2;
    background: rgba(0, 0, 0, 0.45);
    color: white;
    backdrop-filter: blur(4px);
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    padding: 3px 7px;
    border-radius: 5px;
  }

  /* ── Card body ── */
  .bbm-event-card__body {
    padding: 18px 20px 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .bbm-event-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .bbm-event-card__desc {
    font-size: 0.8rem;
    color: #64748b;
    margin: 0;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .bbm-event-card__metas {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 2px;
  }

  .bbm-event-card__meta {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    color: #64748b;

    .material-symbols-outlined {
      font-size: 14px;
      color: #cbd5e1;
      flex-shrink: 0;
    }
  }

  .bbm-event-card__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  .bbm-event-card__tag {
    font-size: 0.68rem;
    font-weight: 600;
    color: #475569;
    background: #f1f5f9;
    padding: 3px 9px;
    border-radius: 9999px;
  }

  /* ── Empty state ── */
  .bbm-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    color: #94a3b8;
    gap: 12px;
  }

  .bbm-empty__icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  .bbm-empty-clear {
    font-size: 0.8rem;
    font-weight: 600;
    color: $bbm-primary;
    background: transparent;
    border: 1.5px solid $bbm-primary;
    border-radius: 9999px;
    padding: 6px 18px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.75;
    }
  }

  /* ── Skeleton ── */
  @keyframes shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .bbm-skeleton-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    overflow: hidden;
  }

  .bbm-skeleton {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 400px 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 4px;
  }

  .bbm-skeleton--image {
    height: 196px;
    border-radius: 0;
  }

  .bbm-skeleton--organiser {
    height: 10px;
    width: 35%;
    margin-bottom: 10px;
  }

  .bbm-skeleton--title {
    height: 20px;
    width: 85%;
    margin-bottom: 10px;
  }

  .bbm-skeleton--text {
    height: 13px;
    width: 90%;
    margin-bottom: 8px;
  }

  @media (max-width: 768px) {
    .bbm-events-hero {
      padding: 40px 16px;
    }
    .bbm-events-main {
      padding: 32px 16px 64px;
    }
    .bbm-filters {
      gap: 5px;
      padding: 10px 12px;
    }
  }
</style>
